import { Immutable } from '@zervant/reactcomponents'
import { ApiError } from 'client/Interfaces/ErrorInterfaces'
import { StateInterface } from 'client/Interfaces/StateInterface'

import { CustomerActions } from '../actions/actionTypes'
import { CustomerCollectionInterface, CustomerInterface } from '../Interfaces/CustomerInterface'
import { ReducerBuilder } from './ReducerBuilder'

const INITIAL_STATE = {
  loaded: null,
  containsLockedCustomers: null,
  quotaInfo: null,
  collection: null,
  customerMap: null,
  lastTouchedId: null,
  error: null,
}
type StateType = CustomerCollectionInterface
type PayloadType = CustomerCollectionInterface | CustomerInterface | ApiError

const flattenMap = (map: Record<string, CustomerInterface[]>): CustomerInterface[] => {
  let customers = [] as CustomerInterface[]
  if (map && Object.keys(map)?.length > 0) {
    for (const [key, value] of Object.entries(map)) {
      customers = customers.concat(value)
    }
  }
  return customers
}

const assignCustomers = (state: StateType, payload: CustomerCollectionInterface): StateType => {
  const { page, pageCount, totalCount, collection } = payload
  const data = Immutable.copy(payload)
  // pagination for customer list
  if (page && pageCount && totalCount && collection) {
    data.pageCount = pageCount
    data.totalCount = totalCount
    let map = { ...state.customerMap }
    if (page) {
      map[page] = payload.collection
    } else {
      map = {}
      map[1] = collection
    }
    data.customerMap = map
    data.collection = flattenMap(map)
  }
  const total = payload.quotaInfo && payload.quotaInfo.total
  // if the quota isn't unlimited
  // we need to set which customers are unavailable
  if (total && total !== -1) {
    let containesLockedCustomers = false
    // we don't need to do the check otherwise
    data.collection
      // descending order by id
      .sort((a, b) => b.id - a.id)
      .map((customer, index) => {
        if (index >= total) {
          customer.unavailable = true
          containesLockedCustomers = true
        }
        return customer
      })
    data.containsLockedCustomers = containesLockedCustomers
  }

  // TOOD: remove when customer tab is refactored
  window.__customers_collection = data
  return { ...state, ...data }
}

function createCustomer(state: StateType, payload: CustomerInterface): StateType {
  const customers = Immutable.copy(state)
  if (customers && customers.collection && !(payload instanceof Error)) {
    customers.error = null
    customers.collection = [payload, ...customers.collection]
    customers.lastTouchedId = payload.id
    return customers
  }
  return customers
}

function updateCustomer(state: StateType, payload: CustomerInterface): StateType {
  let finalizedCustomers: StateType
  const customers = Immutable.copy(state)
  if (customers && customers.collection && !(payload instanceof Error)) {
    customers.error = null
    customers.collection = customers.collection.map((c) => (c.id === payload.id ? payload : c))
    customers.lastTouchedId = payload.id
    finalizedCustomers = assignCustomers(state, customers)
    return finalizedCustomers
  }
  return customers
}

function customerSavingFailed(state: StateType, payload: ApiError): StateType {
  const customers = Immutable.copy(state)
  customers.error = payload
  return customers
}

function resetCustomer(state: StateType, payload: any): StateType {
  const newState = Immutable.copy(state)
  newState.customerMap = null
  newState.collection = null
  return INITIAL_STATE
}

const reducer = ReducerBuilder.create<StateType, PayloadType>(INITIAL_STATE)
  .mapAction(CustomerActions.GET_CUSTOMERS, assignCustomers)
  .mapAction(CustomerActions.GET_ALL_CUSTOMERS, assignCustomers)
  .mapAction(CustomerActions.CREATE_CUSTOMER, createCustomer)
  .mapAction(CustomerActions.UPDATE_CUSTOMER, updateCustomer)
  .mapAction(CustomerActions.UPDATE_CUSTOMER_FAILED, customerSavingFailed)
  .mapAction(CustomerActions.CREATE_CUSTOMER_FAILED, customerSavingFailed)
  .mapAction(CustomerActions.RESET_CUSTOMER, resetCustomer)
  .build()

export default reducer

// selectors
export const validateReceiverForSending = (receiver: CustomerInterface): boolean => {
  let result = false
  if (receiver.address) {
    result = ['streetAddress1', 'pobox', 'city', 'country'].every((x) => Boolean(receiver.address[x]))
  }
  return result
}

export const customersSelector = (state: StateInterface): CustomerCollectionInterface => state.customers
